import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSwipeable } from 'react-swipeable';
import { Typography, Row, Col, Card, Image, Tag, Button, Space, Tabs, Divider, Select, Input, Form, Tooltip, Spin, message } from 'antd';
import { LeftOutlined, RightOutlined, ShareAltOutlined, WhatsAppOutlined, WechatOutlined, QuestionCircleOutlined, CalculatorOutlined, SearchOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { getProductById } from '../../services/productService';
import BackButton from '../../components/BackButton';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const DetailSection = styled.div`
  padding: 24px;
  background: #fff;
  min-height: calc(100vh - 64px);
  margin-top: 64px;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const ImageContainer = styled.div`
  width: 100%;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: row;
  gap: 24px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  height: fit-content;
  position: relative;

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
  }
`;

const ImageSlider = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const ImageSliderTrack = styled.div`
  display: flex;
  width: ${props => props.totalImages * 100}%;
  height: 100%;
  transform: translateX(${props => props.offset}%);
  transition: transform 0.3s ease-out;

  .ant-image {
    width: ${props => 100 / props.totalImages}%;
    height: 100%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-image-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const MainImageWrapper = styled.div`
  flex: 1;
  position: relative;
  height: 480px;
  background: #fafafa;
  border-radius: 8px;
  overflow: hidden;
  touch-action: pan-y pinch-zoom;

  @media (max-width: 768px) {
    height: 400px;
  }
`;

const ImageSlideTrack = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform ${props => props.isSwiping ? '0s' : '0.3s'} ease-out;
  transform: translateX(${props => props.offset}px);
`;

const ImageSlide = styled.div`
  flex: 0 0 100%;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  
  .ant-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ant-image-img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const SwipeIndicator = styled.div`
  position: absolute;
  bottom: 16px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 2;
  padding: 4px 8px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.2);
`;

const SwipeDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.active ? '#D4303B' : 'rgba(255, 255, 255, 0.6)'};
  transition: all 0.3s ease;
`;

const SwipeArrow = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px;
  height: 40px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  z-index: 2;
  transition: all 0.3s ease;
  opacity: 0;
  
  ${props => props.direction === 'left' ? 'left: 16px;' : 'right: 16px;'}

  &:hover {
    background: rgba(0, 0, 0, 0.5);
  }

  @media (max-width: 768px) {
    display: none;
  }

  ${MainImageWrapper}:hover & {
    opacity: 1;
  }
`;

const ThumbnailList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 90px;
  overflow-y: auto;
  padding-right: 12px;
  max-height: 480px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: #f0f0f0;
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    flex-direction: row;
    width: 100%;
    max-height: unset;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 8px;
    padding-right: 0;

    &::-webkit-scrollbar {
      height: 4px;
    }
  }
`;

const Thumbnail = styled.div`
  width: 90px;
  height: 90px;
  flex-shrink: 0;
  border: 2px solid ${props => props.active ? '#D4303B' : 'transparent'};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &:hover {
    border-color: #D4303B;
    opacity: 0.9;
    transform: translateY(-2px);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PriceTag = styled.div`
  color: #D4303B;
  font-size: 28px;
  font-weight: bold;
  margin: 16px 0 12px;
`;

const ShareButton = styled(Button)`
  width: 100%;
  height: 44px;
  margin-top: 8px;
  font-size: 16px;
  border-radius: 8px;
  
  &.whatsapp {
    background: #25D366;
    border-color: #25D366;
    &:hover {
      background: #128C7E;
      border-color: #128C7E;
    }
  }
  
  &.wechat {
    background: #07C160;
    border-color: #07C160;
    &:hover {
      background: #06AD56;
      border-color: #06AD56;
    }
  }
`;

const SizeTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0;
  
  th, td {
    border: 1px solid #f0f0f0;
    padding: 12px;
    text-align: center;
  }
  
  th {
    background: #fafafa;
  }
`;

const PriceCalculator = styled(Card)`
  margin-top: 24px;
  background: #f8f8f8;
  
  .calculator-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
  }
  
  .ant-form-item {
    margin-bottom: 12px;
  }
`;

const CalculatorResult = styled.div`
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  margin-top: 16px;
  
  .result-item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    
    &.total {
      font-weight: bold;
      color: #D4303B;
      font-size: 18px;
      margin-top: 8px;
      padding-top: 8px;
      border-top: 1px dashed #eee;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 64px);
  margin-top: 64px;
`;

const ProductInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 16px;
    gap: 20px;
  }
`;

const InfoSection = styled.div`
  background: #fafafa;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const InfoTitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin: 16px 0 0;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
  
  th, td {
    padding: 16px;
    text-align: left;
    border-bottom: 1px solid #f0f0f0;
  }

  th {
    background: #fafafa;
    font-weight: 500;
    color: #666;
  }

  td {
    font-size: 16px;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const SizeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(65px, 1fr));
  gap: 8px;
  margin-top: 12px;
`;

const SizeButton = styled(Button)`
  width: 100%;
  height: 36px;
  padding: 0;
  font-size: 14px;
`;

const ProductTitle = styled(Title)`
  &.ant-typography {
    font-size: 24px;
    margin-bottom: 12px;
  }
`;

const ProductDescription = styled(Text)`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
  margin-top: 16px;
  display: block;
`;

// 添加汇率配置（与 ProductCard 中的相同）
const EXCHANGE_RATES = {
  CNY: 1,
  USD: 0.14, // 1 CNY = 0.14 USD
};

const formatCurrency = (amount, currency) => {
  const formatter = new Intl.NumberFormat(currency === 'USD' ? 'en-US' : 'zh-CN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount);
};

// 更新尺码转换配置,使用标准对照表
const SIZE_CONVERSION = {
  CN: {
    '35.5': '3.5',
    '36': '4',
    '36.5': '4.5',
    '37.5': '5',
    '38': '5.5',
    '38.5': '6',
    '39': '6.5',
    '40': '7',
    '40.5': '7.5',
    '41': '8',
    '42': '8.5',
    '42.5': '9',
    '43': '9.5',
    '44': '10',
    '44.5': '10.5',
    '45': '11',
    '45.5': '11.5',
    '46': '12',
    '46.5': '12.5',
    '47.5': '13',
    '48': '13.5'
  },
  US: {
    '3.5': '35.5',
    '4': '36',
    '4.5': '36.5',
    '5': '37.5',
    '5.5': '38',
    '6': '38.5',
    '6.5': '39',
    '7': '40',
    '7.5': '40.5',
    '8': '41',
    '8.5': '42',
    '9': '42.5',
    '9.5': '43',
    '10': '44',
    '10.5': '44.5',
    '11': '45',
    '11.5': '45.5',
    '12': '46',
    '12.5': '46.5',
    '13': '47.5',
    '13.5': '48'
  }
};

const convertSize = (size, targetSystem) => {
  // 如果是中文，显示中国尺码
  if (targetSystem === 'CN') {
    return size;
  }
  // 如果是英文，显示美码
  return SIZE_CONVERSION.CN[size] || size;
};

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [activeTab, setActiveTab] = useState('1');
  const [calculatorForm] = Form.useForm();
  const [calculatorResult, setCalculatorResult] = useState(null);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchMove, setTouchMove] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [slideOffset, setSlideOffset] = useState(0);
  const wrapperRef = useRef(null);

  // 添加配送方式数据
  const shippingMethods = [
    { label: t('productDetail.calculator.shipping.standard'), value: 'standard', price: 50, days: '15-20' },
    { label: t('productDetail.calculator.shipping.express'), value: 'express', price: 120, days: '7-10' },
    { label: t('productDetail.calculator.shipping.dhl'), value: 'dhl', price: 200, days: '3-5' },
  ];

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await getProductById(id);
        if (!data) {
          message.error(t('productDetail.error.notFound'));
          navigate('/');
          return;
        }
        setProduct(data);
        document.title = `${data.title} - SHCLUB`;
      } catch (error) {
        console.error('Error fetching product:', error);
        message.error(t('productDetail.error.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id, navigate, t]);

  const handleShare = (platform) => {
    if (platform === 'whatsapp') {
      // WhatsApp手机号码 - 去掉所有空格和特殊字符
      const phoneNumber = '8617602266901'; // 去掉+号，保留国家代码和电话号码
      
      // 构建WhatsApp链接 - 包含商品信息
      const message = encodeURIComponent(`我对这个商品感兴趣: ${displayTitle} ${window.location.href}`);
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
      
      // 在新窗口打开WhatsApp链接
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
      
      // 记录分析数据
      console.log('WhatsApp share clicked:', {
        productId: id,
        productTitle: displayTitle,
        phoneNumber
      });
    } else if (platform === 'wechat') {
      // 微信分享功能 - 可以展示二维码或提示信息
      message.info(t('productDetail.share.wechatInfo') || '请保存图片，打开微信扫一扫分享');
      
      // 这里可以实现展示二维码的逻辑
      console.log('Share to WeChat', {
        productId: id,
        productTitle: displayTitle,
        productUrl: window.location.href
      });
    }
  };

  const handleCalculatorSubmit = (values) => {
    if (!product) return;

    const basePrice = product.price;
    const rate = EXCHANGE_RATES[values.currency];
    const shipping = shippingMethods.find(m => m.value === values.shipping).price;
    
    // 计算结果
    const priceInCurrency = basePrice * rate;
    const shippingInCurrency = shipping * rate;
    let discount = 0;
    
    // 计算优惠（示例：订单满1000减100）
    if (priceInCurrency >= 1000) {
      discount = 100 * rate;
    }
    
    const total = priceInCurrency + shippingInCurrency - discount;
    
    setCalculatorResult({
      currency: values.currency,
      basePrice: priceInCurrency.toFixed(2),
      shipping: shippingInCurrency.toFixed(2),
      discount: discount.toFixed(2),
      total: total.toFixed(2),
      deliveryTime: shippingMethods.find(m => m.value === values.shipping).days
    });
  };

  // 修改预览图片列表处理函数
  const getPreviewImages = () => {
    return product?.images.map(image => ({
      src: image.url,
      alt: image.alt
    })) || [];
  };

  // 获取多语言显示内容的辅助函数
  const getLocalizedContent = (content, translationKey) => {
    if (currentLang !== 'zh' && content?.translations?.[translationKey]) {
      return content.translations[translationKey];
    }
    return content[translationKey];
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
    setIsSwiping(true);
    setTouchMove(0);
  };

  const handleTouchMove = (e) => {
    if (!isSwiping) return;
    const currentTouch = e.touches[0].clientX;
    const diff = currentTouch - touchStart;
    setTouchMove(diff);
    
    // 计算当前偏移量
    const wrapperWidth = wrapperRef.current?.offsetWidth || 0;
    const newOffset = -activeImageIndex * wrapperWidth + diff;
    setSlideOffset(newOffset);
  };

  const handleTouchEnd = () => {
    if (!isSwiping) return;
    
    const wrapperWidth = wrapperRef.current?.offsetWidth || 0;
    const movePercent = (touchMove / wrapperWidth) * 100;
    
    // 如果滑动距离超过20%，则切换图片
    if (Math.abs(movePercent) > 20) {
      if (movePercent > 0) {
        handlePrevImage();
      } else {
        handleNextImage();
      }
    }

    // 重置状态
    setIsSwiping(false);
    setTouchMove(0);
    setSlideOffset(-activeImageIndex * wrapperWidth);
  };

  const handlePrevImage = () => {
    setActiveImageIndex((prev) => {
      const newIndex = prev === 0 ? product.images.length - 1 : prev - 1;
      const wrapperWidth = wrapperRef.current?.offsetWidth || 0;
      setSlideOffset(-newIndex * wrapperWidth);
      return newIndex;
    });
  };

  const handleNextImage = () => {
    setActiveImageIndex((prev) => {
      const newIndex = prev === product.images.length - 1 ? 0 : prev + 1;
      const wrapperWidth = wrapperRef.current?.offsetWidth || 0;
      setSlideOffset(-newIndex * wrapperWidth);
      return newIndex;
    });
  };

  // 监听窗口大小变化，更新偏移量
  useEffect(() => {
    const handleResize = () => {
      const wrapperWidth = wrapperRef.current?.offsetWidth || 0;
      setSlideOffset(-activeImageIndex * wrapperWidth);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, [activeImageIndex]);

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    );
  }

  if (!product) {
    return null;
  }

  // 获取本地化的商品信息
  const displayTitle = getLocalizedContent(product, 'title');
  const displayDescription = getLocalizedContent(product, 'description');
  
  // 处理货币转换
  const currency = currentLang === 'zh' ? 'CNY' : 'USD';
  const exchangeRate = EXCHANGE_RATES[currency];
  const convertedPrice = product.price * exchangeRate;
  const formattedPrice = formatCurrency(convertedPrice, currency);

  // 处理对比价格转换
  const convertedOriginalPrice = product.comparison.original.price * exchangeRate;
  const convertedReplicaPrice = product.comparison.replica.price * exchangeRate;
  const formattedOriginalPrice = formatCurrency(convertedOriginalPrice, currency);
  const formattedReplicaPrice = formatCurrency(convertedReplicaPrice, currency);
  
  // 处理 tags，确保它始终是数组
  let displayTags = [];
  try {
    const rawTags = currentLang !== 'zh' && product.translations?.tags
      ? product.translations.tags
      : product.tags;
    
    console.log('Processing tags in ProductDetail:', {
      originalTags: product.tags,
      translatedTags: product.translations?.tags,
      rawTags,
      type: typeof rawTags,
      isArray: Array.isArray(rawTags)
    });

    if (Array.isArray(rawTags)) {
      displayTags = [...rawTags];
    } else if (typeof rawTags === 'string') {
      displayTags = rawTags.split(',').map(tag => tag.trim());
    } else if (rawTags) {
      displayTags = String(rawTags).split(',').map(tag => tag.trim());
    }
  } catch (error) {
    console.error('Error processing tags:', error);
    displayTags = [];
  }

  // 获取本地化的对比信息
  const displayComparison = {
    original: {
      price: product.comparison.original.price,
      material: currentLang !== 'zh' && product.translations?.comparison?.original?.material
        ? product.translations.comparison.original.material
        : product.comparison.original.material,
      craftsmanship: currentLang !== 'zh' && product.translations?.comparison?.original?.craftsmanship
        ? product.translations.comparison.original.craftsmanship
        : product.comparison.original.craftsmanship
    },
    replica: {
      price: product.comparison.replica.price,
      material: currentLang !== 'zh' && product.translations?.comparison?.replica?.material
        ? product.translations.comparison.replica.material
        : product.comparison.replica.material,
      craftsmanship: currentLang !== 'zh' && product.translations?.comparison?.replica?.craftsmanship
        ? product.translations.comparison.replica.craftsmanship
        : product.comparison.replica.craftsmanship
    }
  };

  // 获取转换后的尺码列表
  const displaySizes = product.sizes.map(size => ({
    original: size,
    converted: convertSize(size, currentLang === 'zh' ? 'CN' : 'US')
  }));

  return (
    <>
      <Helmet>
        <title>{`${displayTitle} - SHCLUB`}</title>
        <meta name="description" content={displayDescription} />
        <meta name="keywords" content={`${displayTitle},${product.category},${displayTags.join(',')},SHCLUB,球鞋,潮牌`} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${displayTitle} - SHCLUB`} />
        <meta property="og:description" content={displayDescription} />
        <meta property="og:image" content={product.images[0]?.url} />
        <meta property="product:price:amount" content={product.price} />
        <meta property="product:price:currency" content={product.currency} />
        
        {/* Twitter */}
        <meta name="twitter:card" content="product" />
        <meta name="twitter:title" content={`${displayTitle} - SHCLUB`} />
        <meta name="twitter:description" content={displayDescription} />
        <meta name="twitter:image" content={product.images[0]?.url} />
      </Helmet>

      <BackButton onClick={() => navigate(-1)} />

      <DetailSection>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <Row gutter={[48, 24]}>
            {/* 商品图片 */}
            <Col xs={24} md={12}>
              <ImageContainer>
                <ThumbnailList>
                  {product.images.map((image, index) => (
                    <Thumbnail
                      key={index}
                      active={index === activeImageIndex}
                      onClick={() => {
                        setActiveImageIndex(index);
                        const wrapperWidth = wrapperRef.current?.offsetWidth || 0;
                        setSlideOffset(-index * wrapperWidth);
                      }}
                    >
                      <img
                        src={image.url}
                        alt={image.alt}
                      />
                    </Thumbnail>
                  ))}
                </ThumbnailList>
                <MainImageWrapper
                  ref={wrapperRef}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                >
                  <ImageSlideTrack
                    isSwiping={isSwiping}
                    offset={slideOffset}
                  >
                    {product.images.map((image, index) => (
                      <ImageSlide key={index}>
                        <Image
                          src={image.url}
                          alt={image.alt}
                          preview={{
                            visible: previewVisible && previewIndex === index,
                            onVisibleChange: (visible) => {
                              setPreviewVisible(visible);
                              if (!visible) {
                                setPreviewIndex(activeImageIndex);
                              }
                            },
                            getContainer: () => document.body
                          }}
                        />
                      </ImageSlide>
                    ))}
                  </ImageSlideTrack>
                  <SwipeArrow direction="left" onClick={handlePrevImage}>
                    <LeftOutlined />
                  </SwipeArrow>
                  <SwipeArrow direction="right" onClick={handleNextImage}>
                    <RightOutlined />
                  </SwipeArrow>
                  <SwipeIndicator>
                    {product.images.map((_, index) => (
                      <SwipeDot key={index} active={index === activeImageIndex} />
                    ))}
                  </SwipeIndicator>
                </MainImageWrapper>
              </ImageContainer>
            </Col>

            {/* 商品信息 */}
            <Col xs={24} md={12}>
              <ProductInfo>
                <div>
                  <ProductTitle level={2}>{displayTitle}</ProductTitle>
                  <Space size={[8, 16]} wrap>
                    {displayTags.map((tag, index) => (
                      <Tag key={index} color={index % 2 ? '#D4303B' : '#282C34'}>
                        {tag}
                      </Tag>
                    ))}
                  </Space>
                  <PriceTag>{formattedPrice}</PriceTag>
                  <ProductDescription>{displayDescription}</ProductDescription>
                </div>

                {/* 尺码选择 */}
                <InfoSection>
                  <InfoTitle>
                    <span>{t('productDetail.size.title')}</span>
                    <Tooltip title={t('productDetail.size.tooltip')}>
                      <QuestionCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                  </InfoTitle>
                  <SizeGrid>
                    {displaySizes.map(({ original, converted }) => (
                      <SizeButton key={original}>
                        {currentLang === 'zh' ? original : `US ${converted}`}
                      </SizeButton>
                    ))}
                  </SizeGrid>
                </InfoSection>

                {/* 商品对比 */}
                <InfoSection>
                  <InfoTitle>{t('productDetail.comparison.title')}</InfoTitle>
                  <ComparisonTable>
                    <thead>
                      <tr>
                        <th></th>
                        <th>{t('productDetail.comparison.original')}</th>
                        <th>{t('productDetail.comparison.replica')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{t('productDetail.comparison.price')}</td>
                        <td>{formattedOriginalPrice}</td>
                        <td>{formattedReplicaPrice}</td>
                      </tr>
                      <tr>
                        <td>{t('productDetail.comparison.material')}</td>
                        <td>{displayComparison.original.material}</td>
                        <td>{displayComparison.replica.material}</td>
                      </tr>
                      <tr>
                        <td>{t('productDetail.comparison.craftsmanship')}</td>
                        <td>{displayComparison.original.craftsmanship}</td>
                        <td>{displayComparison.replica.craftsmanship}</td>
                      </tr>
                    </tbody>
                  </ComparisonTable>
                </InfoSection>

                {/* 价格计算器 */}
                <InfoSection>
                  <InfoTitle>
                    <CalculatorOutlined />
                    <span>{t('productDetail.calculator.title')}</span>
                    <Tooltip title={t('productDetail.calculator.tooltip')}>
                      <QuestionCircleOutlined style={{ color: '#999' }} />
                    </Tooltip>
                  </InfoTitle>
                  <Form
                    form={calculatorForm}
                    onFinish={handleCalculatorSubmit}
                    initialValues={{
                      currency: 'CNY',
                      shipping: 'standard'
                    }}
                  >
                    <Form.Item name="currency" label={t('productDetail.calculator.currency.label')}>
                      <Select>
                        <Select.Option value="CNY">{t('productDetail.calculator.currency.cny')}</Select.Option>
                        <Select.Option value="USD">{t('productDetail.calculator.currency.usd')}</Select.Option>
                        <Select.Option value="EUR">{t('productDetail.calculator.currency.eur')}</Select.Option>
                        <Select.Option value="GBP">{t('productDetail.calculator.currency.gbp')}</Select.Option>
                      </Select>
                    </Form.Item>
                    
                    <Form.Item name="shipping" label={t('productDetail.calculator.shipping.label')}>
                      <Select>
                        {shippingMethods.map(method => (
                          <Select.Option key={method.value} value={method.value}>
                            {method.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>

                    <Button type="primary" onClick={() => calculatorForm.submit()} block>
                      {t('productDetail.calculator.calculate')}
                    </Button>

                    {calculatorResult && (
                      <CalculatorResult>
                        <div className="result-item">
                          <span>{t('productDetail.calculator.result.productPrice')}:</span>
                          <span>{calculatorResult.basePrice} {calculatorResult.currency}</span>
                        </div>
                        <div className="result-item">
                          <span>{t('productDetail.calculator.result.shippingFee')}:</span>
                          <span>{calculatorResult.shipping} {calculatorResult.currency}</span>
                        </div>
                        <div className="result-item">
                          <span>{t('productDetail.calculator.result.discount')}:</span>
                          <span>-{calculatorResult.discount} {calculatorResult.currency}</span>
                        </div>
                        <div className="result-item total">
                          <span>{t('productDetail.calculator.result.total')}:</span>
                          <span>{calculatorResult.total} {calculatorResult.currency}</span>
                        </div>
                        <Text type="secondary">
                          {t('productDetail.calculator.result.estimatedDelivery', { days: calculatorResult.deliveryTime })}
                        </Text>
                      </CalculatorResult>
                    )}
                  </Form>
                </InfoSection>

                {/* 分享按钮 */}
                <ShareButton
                  type="primary"
                  icon={<WhatsAppOutlined />}
                  className="whatsapp"
                  onClick={() => handleShare('whatsapp')}
                >
                  {t('productDetail.share.whatsapp')}
                </ShareButton>
                <ShareButton
                  type="primary"
                  icon={<WechatOutlined />}
                  className="wechat"
                  onClick={() => handleShare('wechat')}
                >
                  {t('productDetail.share.wechat')}
                </ShareButton>
              </ProductInfo>
            </Col>
          </Row>
        </div>
      </DetailSection>
    </>
  );
};

export default ProductDetail; 